import React, {Component} from 'react';
import Helpers from './Utils/Helpers.js';
import 'typeface-roboto';
import {Helmet} from "react-helmet"; // Meta tag and title injector
import {Settings, InventoryTheme, DefaultSearchFields, DefaultSearchHandler} from './Utils/Settings.js';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { Route, withRouter } from "react-router-dom";
import PrimaryInventorySelector from './Components/PrimaryInventorySelector.js';
import InventorySelector from './Components/InventorySelector.js';
import InventorySearchFields from "./Components/SearchFields.js";
import InventorySearchResults from "./Components/SearchResults.js";
import DetailsTrailer from "./Components/DetailsTrailer.js";
import DetailsCargo from "./Components/DetailsCargo";
import DetailsTruck from "./Components/DetailsTruck";
import DetailsPart from "./Components/DetailsPart";
import DetailsInventory from "./Components/DetailsInventory";
import ScrollMemory from "react-router-scroll-memory";

const queryString = require('query-string'); // helpers for parsing the querystring

class App extends Component {

    constructor(props, context) {

        // Note: The primary initiator of state changes comes from react-router and URL changes
        // 1. A component will update the URL. Like the search component or next/back paging buttons.
        // 2. The app.js detects the URL update in componentDidUpdate() and downloads the necessary data.
        // 3. The components receive the data

        super(props, context);

        // Empty defaults for everything
        this.state = {
            inventoryType: Settings.inventoryTypes.NONE, // Horses, trailers, inventory, etc
            searchFieldsIsLoading: false,
            searchFields: { fields: []},
            searchFieldsPage: 1,
            searchFieldsError: null,
            searchFieldsCategoryName: "",

            searchResultsIsLoading: false,
            searchResultsError: null,
            searchResults: null,

            searchDetailsRecord: null,

            inventoryCategory: 0, // Category of the modern inventory system (Ranchjunk and other sites)
            inventoryCategories: []
        }
    }

    componentDidMount() {
        this.populateStateFromRoute(false);
    }

    componentDidUpdate(prevProps) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            window.scrollTo(0, 0);
            this.populateStateFromRoute();
        }
        else if (this.props.location.search !== prevProps.location.search) {
            window.scrollTo(0, 0);
            this.populateStateFromRoute(true);
        }
    }

    populateStateFromRoute(setDefaults) {
        // Load initial values from the route properties
        let it = Settings.inventoryTypes.NONE;
        if (this.props.location.pathname.startsWith("/search")) {
            if (this.props.location.pathname.startsWith("/search/horse-trailers")) {
                it = Settings.inventoryTypes.TRAILER;
            }
            if (this.props.location.pathname.startsWith("/search/cargo-trailers")) {
                it = Settings.inventoryTypes.CARGO;
            }
            if (this.props.location.pathname.startsWith("/search/trucks")) {
                it = Settings.inventoryTypes.TRUCK;
            }
            if (this.props.location.pathname.startsWith("/search/parts")) {
                it = Settings.inventoryTypes.PART;
            }

            // Only reload the search fields and results if we've changed types, or it is specifically requested to do so
            if (it !== this.state.inventoryType || setDefaults) {
                this.setState({
                    inventoryType: it,
                    searchResults: null,
                    searchDetailsRecord: null
                });
                this.populateSearchFields(it, this.props.location.search);
            }
        }

        if (this.props.location.pathname.startsWith("/inventory")) {
            if (this.props.location.pathname === "/inventory") {
                this.populateInventoryCategories();
            }
            if (this.props.location.pathname.startsWith("/inventory")) {
                it = Settings.inventoryTypes.INVENTORY;
                let inventoryCategory = this.props.location.pathname.substring("/inventory/".length);

                // Only reload the search fields and results if we've changed types, or it is specifically requested to do so
                if (setDefaults || inventoryCategory !== "" && ((it !== this.state.inventoryType) || (inventoryCategory !== this.state.inventoryCategory ))) {
                    this.populateSearchFields(it, this.props.location.search, inventoryCategory);
                    this.setState({
                        inventoryType: it,
                        searchResults: null,
                        inventoryCategory: inventoryCategory,
                        searchDetailsRecord: null
                    });
                }
            }
        }

        if (this.props.location.pathname.startsWith("/trailer/details/")) {
            this.setState({
                searchDetailsRecord: null,
            });
            let listingId = Helpers.getListingId(this.props.location.pathname, "/trailer/details/");
            this.populateDetails(Settings.inventoryTypes.TRAILER, listingId);
        }

        if (this.props.location.pathname.startsWith("/cargo/details/")) {
            this.setState({
                searchDetailsRecord: null,
            });
            let listingId = Helpers.getListingId(this.props.location.pathname, "/cargo/details/");
            this.populateDetails(Settings.inventoryTypes.CARGO, listingId);
        }

        if (this.props.location.pathname.startsWith("/truck/details/")) {
            this.setState({
                searchDetailsRecord: null,
            });
            let listingId = Helpers.getListingId(this.props.location.pathname, "/truck/details/");
            this.populateDetails(Settings.inventoryTypes.TRUCK, listingId);
        }

        if (this.props.location.pathname.startsWith("/part/details/")) {
            this.setState({
                searchDetailsRecord: null,
            });
            let listingId = Helpers.getListingId(this.props.location.pathname, "/part/details/");
            this.populateDetails(Settings.inventoryTypes.PART, listingId);
        }

        if (this.props.location.pathname.startsWith("/listing/details/")) {
            this.setState({
                searchDetailsRecord: null,
            });
            let listingId = Helpers.getListingId(this.props.location.pathname, "/listing/details/");
            this.populateDetails(Settings.inventoryTypes.INVENTORY, listingId);
        }
    }

    // Only called if the inventory type changes. Updates the default search fields based on the inventory type.
    // Also populates default choices.
    populateSearchFields(newInventoryType, defaultsString, inventoryCategory) {
        // https://reactjs.org/docs/faq-ajax.html
        let promise = DefaultSearchFields.getFields(newInventoryType, inventoryCategory);
        promise.then(
            (result) => {
                // Apply querystring defaults
                let page = 0;
                const defaults = queryString.parse(defaultsString);
                for (let key in defaults) {

                    if (key === "sort") {
                        let filterId = defaults[key];
                        result.fields.find(x => x.isBeingSorted).isBeingSorted = false;
                        let sortField = result.fields.find(x => x.filterId === filterId);
                        if (sortField) {
                            sortField.isBeingSorted = true;

                            let sortDirection = defaults["sortDirection"];
                            if (sortDirection) {
                                sortField.sortDirection = sortDirection;
                            }
                        }
                    }

                    if (key.startsWith("opt")) {
                        let filterId = key.substring(3);
                        let filterField = result.fields.find(x => x.filterId === filterId);
                        if (filterField) {
                            if (filterField.fieldType === "multiselect") {
                                if (Array.isArray(defaults[key])) {
                                    filterField.selectedValues = defaults[key];
                                } else {
                                    filterField.selectedValues = [defaults[key]];
                                }
                            } else {
                                filterField.selectedValue = defaults[key];
                            }
                        }
                    }

                    if (key.startsWith("zip")) {
                        let filterField = result.fields.find(x => x.filterId === "zip");
                        if (filterField)
                        {
                            filterField.selectedValue = defaults[key];
                        }
                    }

                    // If the page parameter is supplied, use it and initiate a search
                    if (key === "page") {
                        page = parseInt(defaults[key]);
                    }

                }
                this.setState({
                    searchFieldsError: null,
                    searchFields: result,
                    searchFieldsPage: page
                })

                if (page > 0) {
                    this.performSearch();
                    //this.handleSearch(page);
                }
            },
            (error) => {
                this.setState({
                    searchFieldsError: error,
                })
            },
        )
    };

    // Retrieves the details for a listing
    populateDetails(inventoryType, listingId) {
        DefaultSearchHandler.GetDetails(inventoryType, listingId)
            .then(
                (result) => {
                    this.setState({
                        searchDetailsRecord: result
                    });
                },
                (error) => {
                    window.alert("An error occurred. Please try again. " + error);
                },
            )
    };

    populateInventoryCategories() {
        let icPromise = DefaultSearchHandler.GetInventoryCategories();
        icPromise.then((result) => {
            this.setState({
                inventoryCategories: result
            });
        });
    }

    // When a user makes a change to a search parameter
    handleSearchFieldChange = (filterId, newValue) => {

        this.setState((previousState, currentProps) => {

            let searchField = previousState.searchFields.fields.find(x=>x.filterId===filterId);

            // Update the previous state with the new values
            if (searchField) {
                if (Array.isArray(newValue)) {
                    searchField.selectedValues = newValue;
                } else {
                    searchField.selectedValue = newValue;
                }

                // Search field bound to a sort parameter
                if (searchField.setFilterIdWhenChanged) {

                    // Deselect all current sort filters
                    previousState.searchFields.fields.find(x => x.isBeingSorted).isBeingSorted = false;

                    // If a search field is bound to a sort field, enable the sort if a search value is being supplied
                    // The user can change this sort using the UI, but the sort will be enabled by default
                    let dependentField = previousState.searchFields.fields.find(x=>x.filterId===searchField.setFilterIdWhenChanged);

                    if (dependentField) {
                        if (newValue.length > 0) {
                            dependentField.isBeingSorted = true;
                            dependentField.disabled = false;
                        } else {
                            // If there is no value, deselect the bound sort field and reset to the search's default
                            previousState.searchFields.fields.find(x => x.isDefaultSort).isBeingSorted = true;
                            dependentField.disabled = true;
                        }
                    }
                }
            }

            // Merge the searchFields state
            return {
                searchFields: previousState.searchFields
            }
        });
    }

    handleSortFieldChange = (filterId) => {
        this.setState((previousState, currentProps) => {

            // Has the sort actually changed?
            let existingSort = previousState.searchFields.fields.find(x=>x.isBeingSorted);
            if (existingSort && existingSort.filterId === filterId) {
                return null;
            }
            previousState.searchFields.fields.find(x=>x.isBeingSorted).isBeingSorted = false;
            previousState.searchFields.fields.find(x=>x.filterId===filterId).isBeingSorted = true;
            this.handleSearch(previousState.searchFieldsPage);

            // Merge the searchFields state
            return {
                searchFields: previousState.searchFields
            }
        });
    };

    // Updates the querystring which will trigger a componentDidUpdate => performSearch
    handleSearch = (page = 1) => {
        this.setState({
            searchResultsIsLoading: true,
        });

        // Update the querystring
        let qsParams = DefaultSearchHandler.BuildSearchQuerystring(this.state.searchFields, page);
        this.props.history.push(this.props.location.pathname + "?" + qsParams);
    };

    performSearch = () => {
        DefaultSearchHandler.PerformSearch(this.state.inventoryType, this.state.searchFields, this.state.searchFieldsPage, this.state.inventoryCategory)
            .then(
                (result) => {
                    this.setState({
                        searchResultsIsLoading: false,
                        searchResultsError: null,
                        searchResults: result.results,
                        searchResultsTotalRecords: result.totalRecords,
                        searchResultsTotalPages: result.totalPages,
                        searchResultsCurrentPage: result.currentPage
                    })
                },
                (error) => {
                    this.setState({
                        searchFieldsPage: 1,
                        searchResultsIsLoading: false,
                        searchResultsError: error,
                        searchResults: [],
                        searchResultsTotalRecords: 0,
                        searchResultsTotalPages: 0,
                        searchResultsCurrentPage: 0
                    })
                },
            )
    }



    render() {
        const {
            inventoryType,
            searchFieldsIsLoading, searchResultsIsLoading,
            searchResultsError, searchFieldsError,
            searchFields,
            searchResults, searchResultsTotalRecords, searchResultsTotalPages, searchResultsCurrentPage,
            searchDetailsRecord,
            inventoryCategories
        } = this.state;

        const primaryInventorySelectorComponent = () => <PrimaryInventorySelector />
        const InventorySelectorComponent = () => <InventorySelector inventoryCategories={inventoryCategories} />
        let loadingGif = Settings.subFolder + 'Content/loading.gif';


        return (
            <div style={{maxWidth:'1100px', margin: "0 auto", padding: "20px"}}>
                <ScrollMemory />
                <Helmet>
                    <title>Remote Inventory</title>
                </Helmet>
                <MuiThemeProvider theme={InventoryTheme}>
                    <CssBaseline />
                    <Route exact path="/" component={primaryInventorySelectorComponent} />
                    <Route exact path="/inventory" component={InventorySelectorComponent} />
                    <Route path="/inventory/:categoryId"  render={(props) =>
                        <React.Fragment>
                            <InventorySearchFields
                                {...props}
                                style={{marginBottom: "10px"}}
                                inventoryType={inventoryType}
                                searchFields={searchFields}
                                searchFieldsError={searchFieldsError}
                                handleSearch={this.handleSearch.bind(this)}
                                handleSearchFieldChange={this.handleSearchFieldChange.bind(this)}
                                handleSortFieldChange={this.handleSortFieldChange.bind(this)}
                            />
                            <InventorySearchResults
                                searchResultsError = {searchResultsError}
                                inventoryType={inventoryType}
                                searchResults={searchResults}
                                searchResultsTotalRecords={searchResultsTotalRecords}
                                searchResultsTotalPages={searchResultsTotalPages}
                                searchResultsCurrentPage={searchResultsCurrentPage}
                                handleSearch={this.handleSearch}
                            />
                        </React.Fragment> }/>

                    <Route path="/search/:searchtype" render={(props) =>
                        <React.Fragment>
                            <InventorySearchFields
                                {...props}
                                style={{marginBottom: "10px"}}
                                inventoryType={inventoryType}
                                searchFields={searchFields}
                                searchFieldsError={searchFieldsError}
                                handleSearch={this.handleSearch.bind(this)}
                                handleSearchFieldChange={this.handleSearchFieldChange.bind(this)}
                                handleSortFieldChange={this.handleSortFieldChange.bind(this)}
                            />
                            <InventorySearchResults
                                searchResultsError = {searchResultsError}
                                inventoryType={inventoryType}
                                searchResults={searchResults}
                                searchResultsTotalRecords={searchResultsTotalRecords}
                                searchResultsTotalPages={searchResultsTotalPages}
                                searchResultsCurrentPage={searchResultsCurrentPage}
                                handleSearch={this.handleSearch}
                            />
                        </React.Fragment> }/>

                    <Route path="/trailer/details/:id" render={(props) =>
                        <DetailsTrailer
                            {...props}
                            searchDetailsRecord={searchDetailsRecord}
                        />
                    } />

                    <Route path="/cargo/details/:id" render={(props) =>
                        <DetailsCargo
                            {...props}
                            searchDetailsRecord={searchDetailsRecord}
                        />
                    } />

                    <Route path="/truck/details/:id" render={(props) =>
                        <DetailsTruck
                            {...props}
                            searchDetailsRecord={searchDetailsRecord}
                        />
                    } />

                    <Route path="/part/details/:id" render={(props) =>
                        <DetailsPart
                            {...props}
                            searchDetailsRecord={searchDetailsRecord}
                        />
                    } />

                    <Route path="/listing/details/:id" render={(props) =>
                        <DetailsInventory
                            {...props}
                            searchDetailsRecord={searchDetailsRecord}
                        />
                    } />

                    <img src={loadingGif} alt="" id="loading-indicator" style={{position: "fixed", right: "20px", bottom: "20px", border: "1px solid #e7e7e7", borderRadius: "2px", padding: "5px", display:(searchFieldsIsLoading || searchResultsIsLoading) ? "inline-block" : "none"}} />
                </MuiThemeProvider>
            </div>
        );
    }
}

export default withRouter(props => <App {...props} />);
